/**
 * Box への分割アップロード（経路B）。
 *
 * ブラウザ → Box の直接転送で、Worker は一切バイト列を通らない。
 * Worker から受け取るのは「セッション情報」と「フォルダ限定のダウンスコープトークン」だけ。
 *
 * 【設計上の制約】
 * - part_size は Box が決めた値に従う。自前で決めない。
 * - パートの digest は SHA-1 の**生バイナリ**の Base64（hex の Base64 ではない）。
 * - commit にはファイル全体の SHA-1 が要る → 逐次ハッシュ（Sha1）で1パスで求める。
 *   全体ハッシュは順序に依存するので、**読み込みとハッシュは必ず先頭から順**に行い、
 *   送信だけを並列化する。
 * - commit が 202 を返すのは正常系。Retry-After に従って同じ本文で再送する。
 */
import { Sha1, sha1Once, toBoxDigest, toHex } from './sha1.ts'

export type UploadSession = {
  id: string
  partSize: number
  totalParts: number
  expiresAt: string
  endpoints: {
    upload_part: string
    commit: string
    abort: string
    list_parts: string
    status: string
  }
}

export type BoxPart = {
  part_id: string
  offset: number
  size: number
  sha1: string
}

export type UploadProgress = {
  phase: 'hashing' | 'uploading' | 'committing' | 'done'
  uploadedBytes: number
  totalBytes: number
  partsCompleted: number
  totalParts: number
  /** 再送が発生したパートの数（UIで警告を出すため）。 */
  retries: number
}

export type CommitHandler = (args: {
  session: UploadSession
  parts: BoxPart[]
  /** ファイル全体の SHA-1（生バイナリの Base64、`sha=` 接頭辞なし）。 */
  fileSha1Base64: string
}) => Promise<UploadResult>

export type UploadOptions = {
  file: File
  session: UploadSession
  accessToken: string
  onProgress?: (progress: UploadProgress) => void
  /** 同時に送るパート数。既定3。 */
  concurrency?: number
  /** パートごとの最大再送回数。既定4。 */
  maxRetries?: number
  signal?: AbortSignal
  /**
   * 確定処理の実装。
   *
   * **本番では必ず Worker 経由の実装を渡すこと。** 未指定だとブラウザから Box の
   * commit を直接叩くが、これは Box の CORS ホワイトリストに引っかかり
   * `403 cors_origin_not_whitelisted` になる（パート送信は通るので、
   * 全部送り切った最後の1回だけ落ちるという最悪の出方をする）。
   * 既定の直接実装はテスト用に残してある。
   */
  commit?: CommitHandler
}

export type UploadResult = {
  fileId: string
  fileName: string
  size: number
  sha1: string
}

export class UploadError extends Error {
  // Node の型ストリップ実行（node --test）は parameter property を解釈できないため、
  // 明示的にフィールドへ代入している。
  readonly partIndex?: number
  readonly status?: number

  constructor(message: string, partIndex?: number, status?: number) {
    super(message)
    this.name = 'UploadError'
    this.partIndex = partIndex
    this.status = status
  }
}

/**
 * Box のエラー本文を読んで、原因が分かるメッセージにする。
 * ステータスコードだけを見せると、CORS 未設定のような設定ミスが延々分からない。
 */
async function describeBoxError(res: Response, what: string): Promise<string> {
  const text = await res.text().catch(() => '')
  let code = ''
  let message = ''
  try {
    const parsed = JSON.parse(text) as { code?: string; message?: string }
    code = parsed.code ?? ''
    message = parsed.message ?? ''
  } catch {
    message = text.slice(0, 200)
  }

  if (code === 'cors_origin_not_whitelisted') {
    // テスト（node）から呼ばれることもあるので location の存在を前提にしない
    const origin = typeof location !== 'undefined' ? location.origin : '(unknown origin)'
    return (
      `${what} が Box の CORS 設定で拒否されました（オリジン: ${origin}）。` +
      ' commit は Worker 経由にしてください（uploadMasterToBox の commit オプション）。' +
      ' パート送信で出た場合は Box Developer Console の CORS 許可オリジンに追加が必要です。'
    )
  }
  return `${what} failed with ${res.status}${code ? ` (${code})` : ''}${message ? `: ${message}` : ''}`
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms)
    signal?.addEventListener(
      'abort',
      () => {
        clearTimeout(timer)
        reject(new UploadError('aborted'))
      },
      { once: true },
    )
  })

/** 1パートを送る。失敗したら指数バックオフで再送する。 */
async function uploadPart(
  options: UploadOptions,
  index: number,
  chunk: Uint8Array,
  offset: number,
  onRetry: () => void,
): Promise<BoxPart> {
  const { session, accessToken, file, maxRetries = 4, signal } = options
  const digest = toBoxDigest(await sha1Once(chunk))
  const last = offset + chunk.length - 1
  const contentRange = `bytes ${offset}-${last}/${file.size}`

  let attempt = 0
  for (;;) {
    signal?.throwIfAborted()
    try {
      const res = await fetch(session.endpoints.upload_part, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/octet-stream',
          'Content-Range': contentRange,
          Digest: digest,
        },
        body: chunk as unknown as BodyInit,
        signal,
      })

      if (res.ok) {
        const body = (await res.json()) as { part: BoxPart }
        return body.part
      }

      // すでに同じレンジが保存済み。list_parts から実物を拾って続行する。
      if (res.status === 409) {
        const existing = await findExistingPart(options, offset)
        if (existing) return existing
      }

      // 429 / 5xx は再送する。4xx（409以外）は再送しても直らない。
      const retryable = res.status === 429 || res.status >= 500 || res.status === 409
      if (!retryable || attempt >= maxRetries) {
        throw new UploadError(
          await describeBoxError(res, `パート ${index + 1}/${session.totalParts} の送信`),
          index,
          res.status,
        )
      }
      const retryAfter = Number(res.headers.get('retry-after'))
      await sleep(
        Number.isFinite(retryAfter) && retryAfter > 0
          ? retryAfter * 1000
          : Math.min(2 ** attempt * 500, 15000),
        signal,
      )
    } catch (e) {
      if (e instanceof UploadError) throw e
      // ネットワーク断など。パートは不変なので、そのまま同じ内容で送り直せる。
      if (attempt >= maxRetries) {
        throw new UploadError(
          `part ${index + 1} failed: ${e instanceof Error ? e.message : String(e)}`,
          index,
        )
      }
      await sleep(Math.min(2 ** attempt * 500, 15000), signal)
    }
    attempt += 1
    onRetry()
  }
}

/** 再開・重複時に、すでにアップロード済みのパートを探す。 */
async function findExistingPart(
  options: UploadOptions,
  offset: number,
): Promise<BoxPart | null> {
  const res = await fetch(`${options.session.endpoints.list_parts}?limit=1000`, {
    headers: { Authorization: `Bearer ${options.accessToken}` },
    signal: options.signal,
  })
  if (!res.ok) return null
  const body = (await res.json()) as { entries: BoxPart[] }
  return body.entries.find((p) => p.offset === offset) ?? null
}

/**
 * ファイルを分割アップロードして確定させる。
 *
 * 進捗は onProgress で通知する。中断は AbortSignal。
 */
export async function uploadMasterToBox(options: UploadOptions): Promise<UploadResult> {
  const { file, session, accessToken, onProgress, concurrency = 3, signal } = options
  const { partSize, totalParts } = session

  const wholeFileHash = new Sha1()
  const parts: BoxPart[] = new Array(totalParts)
  const progress: UploadProgress = {
    phase: 'hashing',
    uploadedBytes: 0,
    totalBytes: file.size,
    partsCompleted: 0,
    totalParts,
    retries: 0,
  }
  const emit = () => onProgress?.({ ...progress })

  const inFlight = new Set<Promise<void>>()

  for (let index = 0; index < totalParts; index++) {
    signal?.throwIfAborted()

    const offset = index * partSize
    const blob = file.slice(offset, Math.min(offset + partSize, file.size))
    const chunk = new Uint8Array(await blob.arrayBuffer())

    // ★ 全体ハッシュは順序依存。必ず先頭から順にここで流す。
    wholeFileHash.update(chunk)

    progress.phase = 'uploading'
    emit()

    const task = uploadPart(options, index, chunk, offset, () => {
      progress.retries += 1
      emit()
    })
      .then((part) => {
        parts[index] = part
        progress.partsCompleted += 1
        progress.uploadedBytes += chunk.length
        emit()
      })
      .finally(() => {
        inFlight.delete(task)
      })

    inFlight.add(task)
    if (inFlight.size >= concurrency) {
      // 1つ空くまで待つ。失敗はここで伝播する。
      await Promise.race(inFlight)
    }
  }

  await Promise.all(inFlight)

  const wholeSha1 = wholeFileHash.digest()
  const fileSha1Base64 = toBoxDigest(wholeSha1).slice('sha='.length)

  // --- commit ---
  progress.phase = 'committing'
  emit()

  // 既定はテスト用の直接 commit。アプリ本体は Worker 経由の実装を渡す。
  const commit = options.commit ?? directBoxCommit(accessToken)
  const result = await commit({ session, parts, fileSha1Base64 })

  progress.phase = 'done'
  emit()
  return { ...result, sha1: result.sha1 || toHex(wholeSha1) }
}

/**
 * ブラウザから Box の commit を直接叩く実装。
 *
 * **テスト専用。** 実運用では Box の CORS ホワイトリストに阻まれる。
 * アプリ本体は Worker 経由の実装（api.ts の commitMasterUpload）を渡すこと。
 */
export const directBoxCommit =
  (accessToken?: string): CommitHandler =>
  async ({ session, parts, fileSha1Base64 }) => {
    for (let attempt = 0; ; attempt++) {
      const res = await fetch(session.endpoints.commit, {
        method: 'POST',
        headers: {
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          'Content-Type': 'application/json',
          Digest: `sha=${fileSha1Base64}`,
        },
        body: JSON.stringify({ parts }),
      })

      if (res.status === 201) {
        const body = (await res.json()) as {
          entries: { id: string; name: string; size: number; sha1: string }[]
        }
        const entry = body.entries[0]
        if (!entry) throw new UploadError('commit returned no file')
        return { fileId: entry.id, fileName: entry.name, size: entry.size, sha1: entry.sha1 }
      }

      if (res.status === 202 && attempt < 30) {
        const retryAfter = Number(res.headers.get('retry-after'))
        await sleep(Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter * 1000 : 2000)
        continue
      }

      throw new UploadError(await describeBoxError(res, 'commit'), undefined, res.status)
    }
  }

/** 中断時にセッションを破棄する（放置しても7日で失効するが、明示的に消す方が行儀が良い）。 */
export async function abortUpload(session: UploadSession, accessToken: string): Promise<void> {
  await fetch(session.endpoints.abort, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  }).catch(() => undefined)
}
